import React, { useState, useEffect, useContext } from "react";
import _ from "lodash";
import Loader from "ui/components/Loader";
import { useQuery, useMutation } from "@apollo/client";
import {
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  TableFooter,
  Button
} from "@mui/material";
import actAndNotify from "ui/lib/actAndNotify";
import NotificationsContext from "ui/contexts/NotificationsContext";
import CurrentUnderwriterMultiFamilyDataQuery from "ui/sites/underwriting/queries/CurrentUnderwriterMultiFamilyDataQuery.graphql";
import UnderwritingRequestMultiFamilyUpdateMutation from "ui/sites/underwriting/queries/UnderwritingRequestMultiFamilyUpdateMutation.graphql";
import UnderwritingRequestMarketDataUpdateMutation from "ui/sites/underwriting/queries/UnderwritingRequestMarketDataUpdateMutation.graphql";
import UnderwritingRequestBidInfoUpdateMutation from "ui/sites/underwriting/queries/UnderwritingRequestBidInfoUpdateMutation.graphql";
import UnderwritingRequestPostDealDataUpdateMutation from "ui/sites/underwriting/queries/UnderwritingRequestPostDealDataUpdateMutation.graphql";

const SECTIONS_TO_FIELDS = {
  multiFamilyData: [
    "propertyName",
    "yearBuilt",
    "acres",
    "units",
    "submarket",
    "assetClass",
    "assetType",
    "priorityStatus"
  ],
  bidInfo: ["dateUnderwritten", "ourBid", "bidPpu", "proformaYieldOnCost"],
  postDealData: [
    "confirmedSalesPrice",
    "ourBidToMarketSpread",
    "marketCapRate",
    "spyIndex"
  ],
  marketData: [
    "broker",
    "brokerage",
    "seller",
    "whisperPrice",
    "whisperPpd",
    "whisperCapRate",
    "offersDue"
  ]
};

const SECTIONS_TO_MUTATIONS = {
  multiFamilyData: UnderwritingRequestMultiFamilyUpdateMutation,
  marketData: UnderwritingRequestMarketDataUpdateMutation,
  bidInfo: UnderwritingRequestBidInfoUpdateMutation,
  postDealData: UnderwritingRequestPostDealDataUpdateMutation
};

const SECTIONS_TO_MUTATION_FIELD = {
  multiFamilyData: "underwritingRequestMultiFamilyDataUpdate",
  marketData: "underwritingRequestMarketDataUpdate",
  bidInfo: "underwritingRequestBidInfoUpdate",
  postDealData: "underwritingRequestPostDealDataUpdate"
};

const SECTIONS_TO_INPUT = {
  multiFamilyData: "multiFamilyUpdateInput",
  marketData: "marketDataUpdateInput",
  bidInfo: "bidInfoUpdateInput",
  postDealData: "postDealDataUpdateInput"
};

const identity = x => x;

const EXTRACT_FIELD = {
  priorityStatus: _.toNumber,
  units: _.toNumber,
  acres: _.toNumber,
  whisperPrice: _.toNumber,
  whisperPpd: _.toNumber,
  whisperCapRate: _.toNumber,
  ourBid: _.toNumber,
  bidPpu: _.toNumber,
  proformaYieldOnCost: _.toNumber,
  confirmedSalesPrice: _.toNumber,
  ourBidToMarketSpread: _.toNumber,
  marketCapRate: _.toNumber,
  spyIndex: _.toNumber
};

function parsedValue(key, val) {
  return _.get(EXTRACT_FIELD, key, identity)(val);
}

function UnderwritingParcelAssessmentContainer({ parcelId }) {
  const { notify } = useContext(NotificationsContext);
  const defaultSection =
    localStorage.getItem("defaultSection") || "multiFamilyData";
  const [selectedSection, setSelectedSection] = useState(defaultSection);
  const { data, loading, refetch } = useQuery(
    CurrentUnderwriterMultiFamilyDataQuery,
    {
      variables: { parcelId }
    }
  );

  const fields = SECTIONS_TO_FIELDS[selectedSection];

  const sections = _.keys(SECTIONS_TO_FIELDS);

  const getSelectedData = (section, data) =>
    section === "multiFamilyData"
      ? _.get(data, "currentUnderwriter.parcel.multiFamilyData", {})
      : _.get(
          data,
          `currentUnderwriter.parcel.currentRequest.multiFamilyData.${section}`,
          {}
        );

  const [mutableFields, setMutableFields] = useState({});

  useEffect(() => {
    setMutableFields({
      ..._.omit(getSelectedData(selectedSection, data), ["__typename", "id"])
    });
  }, [data, selectedSection]);

  const underwritingRequestId = _.get(
    data,
    "currentUnderwriter.parcel.currentRequest.id"
  );

  const [mutate, { loading: mutationLoading }] = useMutation(
    SECTIONS_TO_MUTATIONS[selectedSection]
  );

  async function save(e) {
    await e.preventDefault();
    if (
      _.get(
        data,
        "currentUnderwriter.parcel.currentRequest.assignedUnderwriter.id"
      ) !== data.currentUnderwriter.id
    ) {
      notify({
        variant: "error",
        message:
          "Can't update multi-family data for a request that isn't yours!"
      });
      return;
    }
    const processedMutableFields = _.fromPairs(
      _.map(mutableFields, (val, key) => [key, parsedValue(key, val)])
    );
    await actAndNotify(mutate, SECTIONS_TO_MUTATION_FIELD[selectedSection], {
      notify,
      mutateOptions: {
        variables: {
          input: {
            underwritingRequestId,
            [SECTIONS_TO_INPUT[selectedSection]]: processedMutableFields
          }
        }
      },
      successMessage: `Saved ${_.startCase(selectedSection)}`
    })();
    await refetch();
  }

  if (loading || mutationLoading) {
    return <Loader />;
  }

  return (
    <Grid container>
      <Grid size={2}>
        <List>
          {sections.map(slug => (
            <ListItem
              button
              key={slug}
              onClick={() => {
                setSelectedSection(slug);
                localStorage.setItem("defaultSection", slug);
              }}
              selected={slug === selectedSection}
            >
              <ListItemText primary={_.startCase(slug)} />
            </ListItem>
          ))}
        </List>
      </Grid>
      <Grid size={10}>
        <Paper elevation={1}>
          <form onSubmit={save}>
            <TableContainer>
              <Table>
                <TableBody>
                  {_.map(fields, key => (
                    <TableRow key={key}>
                      <TableCell>
                        <TextField
                          margin="dense"
                          label={_.startCase(key)}
                          onChange={e =>
                            setMutableFields({
                              ...mutableFields,
                              ...{ [key]: e.target.value }
                            })
                          }
                          fullWidth
                          value={_.get(mutableFields, key, "")}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell align="right">
                      <Button type="submit" variant="contained">
                        Update
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </form>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default UnderwritingParcelAssessmentContainer;
